import { DatePipe, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, ViewChild, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';

import type { OrderSummary } from '@asset-solutions/customer-portal/orders/models';
import { TripStatusPillComponent } from '@asset-solutions/customer-portal/orders/ui';
import { RefreshTimePipe, downloadCSV } from '@asset-solutions/shared/utilities';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import { OrderColumnIndex } from './orders-downloader';
import { SensorDeviationPipe } from './sensor-deviation.pipe';
import { TempRangeComponent } from './temp-range.component';
import { OrderSummaryVM } from '../order-summary-vm';

@Component({
  selector: 'orders-table',
  templateUrl: './orders-table.component.html',
  styleUrls: ['./orders-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MatTableModule,
    MatPaginatorModule,
    MatIcon,
    MatTooltip,
    TempRangeComponent,
    TripStatusPillComponent,
    MatSortModule,
    RefreshTimePipe,
    DecimalPipe,
    SensorDeviationPipe,
    DatePipe,
    FontAwesomeModule
  ]
})
export class OrdersTableComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @Input() set orders(orders: OrderSummary[]) {
    this.length = orders.length;
    this.dataSource.data = orders
      .map((order) => OrderSummaryVM.create(order))
      .sort((a, b) => b.orderPriority - a.orderPriority);
    this.lastUpdated = this.length ? new Date().toISOString() : undefined;
  }
  @Input() set orderColumns(orderColumns: string[]) {
    this.columns = ['detailsLink'].concat(orderColumns);
  }

  private router = inject(Router);
  protected columns: string[] = [];
  protected dataSource = new MatTableDataSource<OrderSummaryVM>();
  protected downloadIcon = faDownload;
  protected lastUpdated?: string = undefined;
  protected length = 0;

  public ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (order, property): string | number => {
      switch (property) {
        case 'bol':
          return order.shipmentDetails.bolNumber || 0;
        case 'customer':
          return order.shipmentDetails.customerName || 0;
        case 'origin':
          return order.origin ? order.origin.name + ', ' + order.origin.cityState : 0;
        case 'originEta':
          return order.origin && order.origin.scheduledETA ? order.origin.scheduledETA : 0;
        case 'nextStop':
          return order.nextStop ? order.nextStop.name + ',' + order.nextStop.cityState : 0;
        case 'nextStopEta':
          return order.nextStop && order.nextStop.scheduledETA ? order.nextStop.scheduledETA : 0;
        case 'destination':
          return order.destination ? order.destination.name + ',' + order.destination.cityState : 0;
        case 'destinationEta':
          return order.destination && order.destination.scheduledETA ? order.destination.scheduledETA : 0;
        case 'tempRange':
          return `${order.trailer.tempMin} to ${order.trailer.tempMax}`;
        case 'sensor1':
          return order.trailer.sensor1 || 0;
        case 'sensor2':
          return order.trailer.sensor2 || 0;
        case 'sensor3':
          return order.trailer.sensor3 || 0;
        case 'pickupTime':
          return order.origin ? order.origin.scheduledArrivalTime : 0;
        case 'deliveryTime':
          return order.destination ? order.destination.scheduledArrivalTime : 0;
        case 'tractor':
          return order.tractor.tractorNumber || 0;
        case 'trailer':
          return order.trailer.trailerId || 0;
        default:
          if (property in order) {
            return (order as any)[property];
          }
          return 0;
      }
    };
    this.dataSource.sort = this.sort;
  }

  public onClick(order: OrderSummary): void {
    this.router.navigate([{ outlets: { details: ['order', order.orderMasterId] } }]);
  }

  public onDownloadOrders(): void {
    const columns = this.columns.slice(1);
    const headers = columns.map((column) => OrderColumnIndex[column].label);

    const data = this.dataSource.sortData(this.dataSource.data, this.sort).map((order) => {
      return columns.map((column) => OrderColumnIndex[column].selectFn(order));
    });
    downloadCSV(data, 'order-list.csv', headers);
  }

  public trackBy(_index: number, order: OrderSummaryVM): number {
    return order.orderMasterId;
  }
}
